// Agents tab — the commissionable partners (territory → level / type / PSM / Stax billing id).
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Search, PauseCircle, Link2, Building2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { apiError, thCls, tdCls } from "./engineShared";

export function EngineAgents({ canManage, levels, reloadOverview }) {
  const [items, setItems] = useState([]);
  const [psms, setPsms] = useState([]);
  const [types, setTypes] = useState([]);
  const [meta, setMeta] = useState({ linked: 0, partners_with_territory: 0 });
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [unlinkedOnly, setUnlinkedOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [edit, setEdit] = useState(null);
  const [linkFor, setLinkFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/commissions/engine/agents");
      setItems(data.items || []); setPsms(data.psms || []); setTypes(data.types || []);
      setMeta({ linked: data.linked || 0, partners_with_territory: data.partners_with_territory || 0 });
    } catch (e) { apiError(e, "Could not load agents"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const linkPartners = async () => {
    setLinking(true);
    try {
      const { data } = await api.post("/commissions/engine/agents/link-partners");
      toast.success(`${data.linked} agent${data.linked === 1 ? "" : "s"} newly linked · ${data.no_partner} without a partner`
        + (data.ambiguous?.length ? ` · ${data.ambiguous.length} territory shared by several partners` : ""));
      load();
    } catch (e) { apiError(e, "Could not link partners"); } finally { setLinking(false); }
  };

  const visible = useMemo(() => {
    const t = q.trim().toLowerCase();
    return items.filter((a) => (showInactive || a.active) && (!unlinkedOnly || !a.org_id) &&
      (!t || (a.name || "").toLowerCase().includes(t) || (a.territory || "").toLowerCase().includes(t)
        || (a.org_name || "").toLowerCase().includes(t) || String(a.billing_customer_id || "").includes(t)));
  }, [items, q, showInactive, unlinkedOnly]);

  return (
    <div className="space-y-3" data-testid="engine-agents">
      <Card className="border-slate-200 p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[240px]">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find an agent, territory or partner" className="h-9 pl-8" data-testid="engine-ag-search" />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="engine-ag-inactive" /> Show inactive</label>
          <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={unlinkedOnly} onCheckedChange={setUnlinkedOnly} data-testid="engine-ag-unlinked" /> Not linked only</label>
          <span className="text-xs text-slate-500" data-testid="engine-ag-count">{visible.length} agents</span>
          <span className="text-xs text-slate-500" data-testid="engine-ag-linked">{meta.linked} linked to a partner</span>
          {canManage && (
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" className="h-9" onClick={linkPartners} disabled={linking} data-testid="engine-ag-link">
                {linking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Link partners
              </Button>
              <Button className="h-9" onClick={() => setEdit({})} data-testid="engine-ag-add"><Plus className="h-4 w-4" /> Add agent</Button>
            </div>
          )}
        </div>
        <p className="mt-2 text-xs text-slate-500">
          The territory is the unique key: it must match the Stax customer <code>reference1</code> prefix (e.g. <code>..beltech.ca</code>) and is also how an agent is linked to its Partner record — the partner whose <b>Territory</b> is the same (with or without the <code>..</code>). Agents no partner claims are left as they are (former agents). Invoices for the agent's own Stax billing account are never commissioned; a paused agent's lines stay on hold.
        </p>
      </Card>
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="engine-ag-table">
            <thead className="bg-slate-50"><tr>
              <th className={thCls}>Agent</th><th className={thCls}>Territory</th><th className={thCls}>Partner</th><th className={thCls}>Type</th><th className={thCls}>Level</th>
              <th className={thCls}>PSM</th><th className={thCls}>Stax billing id</th><th className={thCls}>Status</th><th className={thCls}></th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /></td></tr>}
              {!loading && visible.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No agents</td></tr>}
              {!loading && visible.map((a) => (
                <tr key={a.agent_id} className={`border-t border-slate-100 hover:bg-slate-50/60 ${a.active ? "" : "opacity-60"}`} data-testid={`engine-ag-row-${a.agent_id}`}>
                  <td className={`${tdCls} font-medium text-slate-800`}>{a.name}{a.email && <div className="text-xs font-normal text-slate-500">{a.email}</div>}</td>
                  <td className={`${tdCls} font-mono text-xs text-slate-700`} data-testid={`engine-ag-territory-${a.agent_id}`}>{a.territory || <span className="font-sans text-rose-600">missing</span>}</td>
                  <td className={tdCls} data-testid={`engine-ag-partner-${a.agent_id}`}>
                    {a.org_id ? (
                      <Link to={`/partners?q=${encodeURIComponent(a.org_name || "")}`} className="inline-flex items-center gap-1 text-slate-800 hover:text-primary hover:underline" title="Open in Partners">
                        <Building2 className="h-3.5 w-3.5 text-slate-400" /> {a.org_name}
                      </Link>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-xs text-slate-400" title={a.territory ? `No partner record has territory ${a.territory}` : "Agent has no territory"}>not linked</span>
                        {canManage && a.territory && (
                          <button type="button" onClick={() => setLinkFor(a)} className="text-xs text-primary hover:underline" data-testid={`engine-ag-pick-${a.agent_id}`}>Link…</button>
                        )}
                      </span>
                    )}
                  </td>
                  <td className={`${tdCls} text-slate-600`}>{a.agent_type}</td>
                  <td className={tdCls}>{a.level_name || "—"}</td>
                  <td className={`${tdCls} text-slate-600`}>{a.psm_name || "—"}</td>
                  <td className={`${tdCls} tabular-nums text-slate-600`}>{a.billing_customer_id || "—"}</td>
                  <td className={tdCls}>
                    {!a.active && <Badge variant="outline" className="text-slate-500">inactive</Badge>}
                    {a.active && a.paused && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><PauseCircle className="mr-1 h-3 w-3" />paused</Badge>}
                    {a.active && !a.paused && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">active</Badge>}
                  </td>
                  <td className={tdCls}>{canManage && <Button variant="ghost" size="sm" onClick={() => setEdit(a)} data-testid={`engine-ag-edit-${a.agent_id}`}><Pencil className="h-3.5 w-3.5" /></Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {edit && <AgentDialog agent={edit} levels={levels} psms={psms} types={types} onClose={() => setEdit(null)}
                            onSaved={() => { setEdit(null); load(); reloadOverview?.(); }} />}
      {linkFor && <LinkPartnerDialog agent={linkFor} onClose={() => setLinkFor(null)}
                                     onLinked={() => { setLinkFor(null); load(); }} />}
    </div>
  );
}

function LinkPartnerDialog({ agent, onClose, onLinked }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/commissions/engine/agents/${agent.agent_id}/partner-candidates`, { params: { q: q || undefined } })
      .then(({ data }) => { if (alive) setItems(data.items || []); })
      .catch((e) => apiError(e, "Could not load partners"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [agent.agent_id, q]);

  const pick = async (o, force = false) => {
    setBusy(o.org_id);
    try {
      await api.post(`/commissions/engine/agents/${agent.agent_id}/link`, { org_id: o.org_id, force });
      toast.success(`${o.name} now carries territory ${agent.territory}`);
      onLinked();
    } catch (e) {
      if (e?.response?.status === 409 && !force && (o.territory || "").trim()
          && window.confirm(`${o.name} already has territory ${o.territory}. Replace it with ${agent.territory}?`)) {
        return pick(o, true);
      }
      apiError(e, "Could not link the partner");
    } finally { setBusy(""); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="engine-link-dialog">
        <DialogHeader>
          <DialogTitle>Link {agent.name} to a partner</DialogTitle>
          <DialogDescription>The chosen partner record gets territory <code>{agent.territory}</code>, which is what ties it to this agent (and scopes its Audit Log).</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search partners by name" className="h-9 pl-8" autoFocus data-testid="engine-link-search" />
        </div>
        <div className="max-h-80 divide-y divide-slate-100 overflow-auto rounded border border-slate-200" data-testid="engine-link-list">
          {loading && <div className="px-3 py-6 text-center text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /></div>}
          {!loading && items.length === 0 && <div className="px-3 py-6 text-center text-sm text-slate-400">No partner looks like this agent — search by name.</div>}
          {!loading && items.map((o) => (
            <div key={o.org_id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`engine-link-opt-${o.org_id}`}>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-slate-800">{o.name}</div>
                <div className="text-xs text-slate-500">{o.partner_type || "No type"}{o.territory ? <> · territory <code>{o.territory}</code></> : " · no territory yet"}</div>
              </div>
              <Button size="sm" variant="outline" className="h-8" disabled={!!busy} onClick={() => pick(o)} data-testid={`engine-link-pick-${o.org_id}`}>
                {busy === o.org_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Use this partner"}
              </Button>
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="engine-link-cancel">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AgentDialog({ agent, levels, psms, types, onClose, onSaved }) {
  const isNew = !agent.agent_id;
  const [f, setF] = useState({
    name: agent.name || "", territory: agent.territory || "", billing_customer_id: agent.billing_customer_id || "",
    agent_type: agent.agent_type || "Agent", level_id: agent.level_id ? String(agent.level_id) : "", psm_id: agent.psm_id ? String(agent.psm_id) : "",
    email: agent.email || "", active: agent.active !== false, paused: !!agent.paused, notes: agent.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, billing_customer_id: f.billing_customer_id ? Number(f.billing_customer_id) : null,
                     level_id: f.level_id ? Number(f.level_id) : null, psm_id: f.psm_id ? Number(f.psm_id) : null };
      if (isNew) await api.post("/commissions/engine/agents", body);
      else await api.put(`/commissions/engine/agents/${agent.agent_id}`, body);
      toast.success(isNew ? "Agent added" : "Agent saved");
      onSaved();
    } catch (e) { apiError(e, "Could not save the agent"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="engine-agent-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Add agent" : "Edit agent"}</DialogTitle>
          <DialogDescription>Level changes apply from the next sync; use “Resync month” to recompute a past month.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="h-9" data-testid="engine-agent-name" /></div>
          <div><Label>Territory</Label><Input value={f.territory} onChange={(e) => set("territory", e.target.value)} placeholder="..example.ca" className="h-9" data-testid="engine-agent-territory" />
            <p className="mt-1 text-[11px] text-slate-500">Saved with the <code>..</code> prefix; links to the partner with this territory.</p></div>
          <div><Label>Stax billing id</Label><Input type="number" value={f.billing_customer_id} onChange={(e) => set("billing_customer_id", e.target.value)} className="h-9" data-testid="engine-agent-billing" /></div>
          <div><Label>Type</Label>
            <Select value={f.agent_type} onValueChange={(v) => set("agent_type", v)}>
              <SelectTrigger className="h-9" data-testid="engine-agent-type"><SelectValue /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Level</Label>
            <Select value={f.level_id || "none"} onValueChange={(v) => set("level_id", v === "none" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="engine-agent-level"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{levels.map((l) => <SelectItem key={l.level_id} value={String(l.level_id)}>{l.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>PSM</Label>
            <Select value={f.psm_id || "none"} onValueChange={(v) => set("psm_id", v === "none" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="engine-agent-psm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{psms.map((p) => <SelectItem key={p.psm_id} value={String(p.psm_id)}>{p.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Email</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} className="h-9" data-testid="engine-agent-email" /></div>
          <div className="col-span-2 flex items-center gap-6 text-sm">
            <label className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => set("active", v)} data-testid="engine-agent-active" /> Active</label>
            <label className="flex items-center gap-2"><Switch checked={f.paused} onCheckedChange={(v) => set("paused", v)} data-testid="engine-agent-paused" /> Commissions paused</label>
          </div>
          <div className="col-span-2"><Label>Notes</Label><Input value={f.notes} onChange={(e) => set("notes", e.target.value)} className="h-9" data-testid="engine-agent-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="engine-agent-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !f.name.trim() || !f.territory.trim()} data-testid="engine-agent-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
